import React, { useState } from 'react';
import { ArrowLeftIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { orgUsers } from '../../data/offboarding';
import type { DeactivationResult } from '../../types/offboarding';
import { AccessDeactivation } from './AccessDeactivation';
import { OffboardingStart } from './OffboardingStart';
import type { OffboardingScope } from './OffboardingStart';
import { OffboardingTool } from './OffboardingTool';
type Stage = 'start' | 'access' | 'reassign';
const USER_STAGES: {
  id: Stage;
  label: string;
}[] = [{
  id: 'start',
  label: 'Select user'
}, {
  id: 'access',
  label: 'Deactivate access'
}, {
  id: 'reassign',
  label: 'Reassign records'
}];
export function OffboardingWorkflow() {
  const [stage, setStage] = useState<Stage>('start');
  const [scope, setScope] = useState<OffboardingScope | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [deactivation, setDeactivation] = useState<DeactivationResult | null>(null);
  const user = orgUsers.find(u => u.id === userId);
  const startOver = () => {
    setStage('start');
    setDeactivation(null);
  };
  return <div className="space-y-4" data-id="element-224" data-mp="14o13eetiopya">
      {scope === 'user' && user && stage !== 'start' && <Progress stage={stage} data-id="element-225" data-mp="19o5neetmw2t9" />}
      {scope === 'records' && stage === 'reassign' && <button type="button" onClick={startOver} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900" data-id="element-226" data-mp="1eo1pu9monm6p">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-227" data-mp="ly2yg7l0ncyv" />
          Change offboarding type
        </button>}

      {stage === 'start' && <OffboardingStart scope={scope} onScopeChange={setScope} userId={userId} onUserChange={setUserId} onContinue={() => setStage(scope === 'user' ? 'access' : 'reassign')} data-id="element-228" data-mp="qw4fkmyvt27p" />}

      {stage === 'access' && user && <AccessDeactivation key={user.id} user={user} onBack={() => setStage('start')} onContinue={result => {
      setDeactivation(result);
      setStage('reassign');
    }} data-id="element-229" data-mp="36idnrr4k8w0" />}

      {stage === 'reassign' && <OffboardingTool key={scope === 'user' && user ? user.id : 'records-only'} initialSourceUserId={scope === 'user' ? user?.id : undefined} banner={scope === 'user' && user && deactivation ? <div className="flex flex-col gap-2 rounded-lg border border-success/25 bg-success/5 px-4 py-3 sm:flex-row sm:items-center" data-id="element-231" data-mp="34cveku530md1">
                <div className="flex flex-1 items-start gap-2.5" data-id="element-232" data-mp="3id63vsqqtsw3">
                  <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" aria-hidden="true" data-id="element-233" data-mp="3dcqutddhv7jx" />
                  <p className="text-[13px] text-gray-800" data-id="element-234" data-mp="xlco96nfto7c">
                    <span className="font-semibold" data-id="element-235" data-mp="xnyxdapz8bro">Access removed for {user.name}</span> —{' '}
                    {deactivation.licensesRemoved} licenses and {deactivation.permissionSetsRemoved}{' '}
                    permission sets. They're pre-selected below as the source owner.
                  </p>
                </div>
                <button type="button" onClick={startOver} className="self-start text-xs font-medium text-navy-600 hover:text-navy-800 hover:underline sm:self-center" data-id="element-236" data-mp="21kj20w219dcg">
                  Offboard someone else
                </button>
              </div> : undefined} data-id="element-230" data-mp="4wyskxjtn3yy" />}
    </div>;
}
function Progress({
  stage
}: {
  stage: Stage;
}) {
  const currentIndex = USER_STAGES.findIndex(s => s.id === stage);
  return <ol className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-label="Offboarding progress" data-id="element-237" data-mp="2pgwxdblo32nq">
      {USER_STAGES.map((s, index) => {
      const done = index < currentIndex;
      const current = index === currentIndex;
      return <li key={s.id} className="flex items-center gap-3" data-id="element-238" data-mp="3i02js226nm3l" data-mpi={index}>
            <span className="flex items-center gap-2 text-[13px] font-medium" aria-current={current ? 'step' : undefined} data-id="element-239" data-mp="1td7x78i9m4xl">
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${done ? 'bg-success text-white' : current ? 'bg-navy-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-240" data-mp="1kb8e8anowqs0">
                {done ? <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-241" data-mp="23ame1983ovqf" /> : index + 1}
              </span>
              <span className={current ? 'text-gray-900' : done ? 'text-gray-600' : 'text-gray-400'} data-id="element-242" data-mp="1ybj87mkaqlhe">
                {s.label}
              </span>
            </span>
            {index < USER_STAGES.length - 1 && <span className="h-px w-6 bg-gray-300" aria-hidden="true" data-id="element-243" data-mp="2qznfgciin6m7" />}
          </li>;
    })}
    </ol>;
}